import { FC } from "react";
import {
  BoxProps,
  Skeleton,
  TabNav,
  TabNavLink,
  TabNavLinkProps,
} from "@hightouch/htui";
import { RouterLink, useLocation } from "src/router";

export type RouteTab = Pick<
  TabNavLinkProps,
  "statusIndicator" | "isDisabled" | "count" | "tooltip"
> & {
  isHidden?: boolean;
  path: string;
  title: string;
};

type Props = BoxProps & {
  isLoading?: boolean;
  tabs: Array<RouteTab>;
  depth?: number;
};

export const useTabState = <T extends RouteTab>(tabs: Array<T>, depth = 3) => {
  const location = useLocation();
  const pathTabs = location.pathname.split("/");

  const visibleTabs = tabs.filter((tab) => !tab.isHidden);

  const activeTabIndex = visibleTabs.findIndex((tab) =>
    tab.path === "" ? pathTabs.length === depth : tab.path === pathTabs[depth],
  );

  const activeTab = visibleTabs[activeTabIndex];

  return {
    visibleTabs,
    activeTab,
    activeTabIndex,
  };
};

export const RouteTabs: FC<Readonly<Props>> = ({
  tabs,
  depth = 3,
  isLoading = false,
  ...props
}) => {
  const { visibleTabs, activeTabIndex } = useTabState(tabs, depth);

  return (
    <Skeleton isLoading={isLoading}>
      <TabNav {...props}>
        {visibleTabs.map((tab, index) => {
          const isActive = index === activeTabIndex;
          return (
            <TabNavLink
              asChild
              key={tab.path === "" ? "index" : tab.path}
              isActive={isActive}
              isDisabled={tab.isDisabled}
              count={tab.count}
              statusIndicator={tab.statusIndicator}
              tooltip={tab.tooltip}
              badgeVariant={isActive ? "info" : "subtle"}
            >
              <RouterLink
                preload={isActive ? undefined : "mount"}
                to={{ pathname: tab.path, search: location.search }}
              >
                {tab.title}
              </RouterLink>
            </TabNavLink>
          );
        })}
      </TabNav>
    </Skeleton>
  );
};
